/**
 * PolygonService
 * Handles polygon plotting, removal, and updates
 * Uses MapManager for actual map operations
 *
 * Key resilience features:
 * - Tracks a `plotVersion` to detect stale fitBounds callbacks.
 * - Re-checks map/style before every Mapbox call.
 * - Separated `plotPolygonShape` and `focusPolygon` for clarity.
 */
import { wktToGeoJSON } from '@terraformer/wkt';
import mapboxgl from 'mapbox-gl';
import { LAYER_PREFIXES, MAP_ANIMATION } from '@/constants/mapConstants';
import { mapManager } from './MapManager';

interface PolygonData {
  wkt: string;
  geojson?: GeoJSON.Polygon;
  bounds?: mapboxgl.LngLatBounds;
}

class PolygonService {
  private static instance: PolygonService;
  private currentPolygon: PolygonData | null = null;
  private polygonId: string = ''; // Empty string - MapManager will use the prefix

  /** Incremented on each plotPolygon call; used to discard stale callbacks */
  private plotVersion = 0;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): PolygonService {
    if (!PolygonService.instance) {
      PolygonService.instance = new PolygonService();
    }
    return PolygonService.instance;
  }

  /**
   * Convert WKT to GeoJSON and validate
   */
  private convertWktToGeoJSON(wkt: string): GeoJSON.Polygon | null {
    try {
      const geojson = wktToGeoJSON(wkt) as GeoJSON.Polygon;

      if (!geojson || !geojson.coordinates || !Array.isArray(geojson.coordinates) || geojson.coordinates.length === 0) {
        console.error('PolygonService: Invalid geojson conversion:', geojson);
        return null;
      }

      const coords = geojson.coordinates[0];
      if (!Array.isArray(coords) || coords.length < 3) {
        console.error('PolygonService: Invalid polygon coordinates:', coords);
        return null;
      }

      return geojson;
    } catch (error) {
      console.error('PolygonService: Error converting WKT to GeoJSON:', error);
      return null;
    }
  }

  /**
   * Calculate bounds from GeoJSON coordinates
   */
  private calculateBounds(coordinates: number[][]): mapboxgl.LngLatBounds | null {
    const bounds = new mapboxgl.LngLatBounds();

    try {
      coordinates.forEach(([lng, lat]) => {
        if (typeof lng === 'number' && typeof lat === 'number' && !Number.isNaN(lng) && !Number.isNaN(lat)) {
          bounds.extend([lng, lat]);
        }
      });

      if (bounds.isEmpty()) {
        console.error('PolygonService: Invalid bounds for polygon');
        return null;
      }

      return bounds;
    } catch (error) {
      console.error('PolygonService: Error calculating bounds:', error);
      return null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Plot a polygon on the map from WKT string
   * @param wkt - WKT polygon string
   * @param options - Optional configuration
   */
  async plotPolygon(
    wkt: string,
    options?: {
      fitBounds?: boolean;
      fillStyle?: mapboxgl.FillLayer['paint'];
      outlineStyle?: mapboxgl.LineLayer['paint'];
      onComplete?: () => void;
    },
  ): Promise<boolean> {
    // Bump version so any in-flight fitBounds from a previous call is ignored
    const version = ++this.plotVersion;

    const map = mapManager.getMap();
    if (!map) {
      console.warn('PolygonService: map not available');
      return false;
    }
    if (!map.isStyleLoaded()) {
      console.warn('PolygonService: style not loaded');
      return false;
    }

    // If same polygon is already active and layers exist, still do fitBounds if requested
    if (this.currentPolygon?.wkt === wkt) {
      const fillLayerId = LAYER_PREFIXES.SELECTED_GEOFENCE_FILL;
      const outlineLayerId = LAYER_PREFIXES.SELECTED_GEOFENCE_OUTLINE;
      if (map.getLayer(fillLayerId) && map.getLayer(outlineLayerId)) {
        // Still do fitBounds for the existing polygon
        if (options?.fitBounds !== false && this.currentPolygon.bounds) {
          this.focusPolygon(map, this.currentPolygon.bounds, version, options?.onComplete);
        } else {
          options?.onComplete?.();
        }
        return true;
      }
    }

    // Convert WKT to GeoJSON
    const geojson = this.convertWktToGeoJSON(wkt);
    if (!geojson) {
      return false;
    }

    // Calculate bounds
    const bounds = this.calculateBounds(geojson.coordinates[0]);
    if (!bounds) {
      return false;
    }

    // Store polygon data
    this.currentPolygon = {
      wkt,
      geojson,
      bounds,
    };

    // Wrap geometry in a Feature for MapManager
    const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
      type: 'Feature',
      properties: {},
      geometry: geojson,
    };

    // Add polygon layers via MapManager
    const success = mapManager.addPolygon(this.polygonId, feature, options?.fillStyle, options?.outlineStyle);

    if (!success) {
      return false;
    }

    // Fit bounds if requested - pass the verified map instance directly
    if (options?.fitBounds !== false && bounds) {
      this.focusPolygon(map, bounds, version, options?.onComplete);
    } else {
      options?.onComplete?.();
    }

    return true;
  }

  /**
   * Focus the map on the given bounds.
   * Guards against stale calls by checking plotVersion.
   * Executes immediately to handle fast clicks properly.
   * @param verifiedMap - Map instance that was already verified as ready
   */
  private focusPolygon(
    verifiedMap: mapboxgl.Map,
    bounds: mapboxgl.LngLatBounds,
    version: number,
    onComplete?: () => void,
  ): void {
    // Stale check - if a newer polygon was selected, skip this fitBounds
    if (this.plotVersion !== version) {
      return;
    }

    try {
      // Stop any ongoing animation before starting a new one
      // This ensures fast clicks don't queue up animations
      verifiedMap.stop();

      verifiedMap.fitBounds(bounds, {
        padding: MAP_ANIMATION.FIT_BOUNDS_PADDING,
        duration: MAP_ANIMATION.FIT_BOUNDS_DURATION,
        minZoom: MAP_ANIMATION.FIT_BOUNDS_MIN_ZOOM,
        maxZoom: MAP_ANIMATION.FIT_BOUNDS_MAX_ZOOM,
        easing: (t) => t * (2 - t),
      });

      verifiedMap.once('moveend', () => {
        // Another stale check after animation
        if (this.plotVersion !== version) {
          return;
        }
        requestAnimationFrame(() => {
          const finalMap = mapManager.getMap();
          if (finalMap) {
            finalMap.triggerRepaint();
          }
          onComplete?.();
        });
      });
    } catch (err) {
      console.error('Polygon: fitBounds error', err);
      onComplete?.();
    }
  }

  /**
   * Remove the current polygon from the map
   */
  removePolygon(): boolean {
    // Bump version to cancel any pending fitBounds
    this.plotVersion++;

    const map = mapManager.getMap();
    if (!map || !map.isStyleLoaded()) {
      this.currentPolygon = null;
      return false;
    }

    const success = mapManager.removePolygon(this.polygonId);
    if (success) {
      this.currentPolygon = null;
    }
    return success;
  }

  /**
   * Update the current polygon
   * @param wkt - New WKT polygon string
   */
  async updatePolygon(wkt: string, options?: { fitBounds?: boolean }): Promise<boolean> {
    if (this.currentPolygon?.wkt === wkt) {
      return true;
    }

    this.removePolygon();
    return this.plotPolygon(wkt, options);
  }

  /**
   * Get the current polygon data
   */
  getCurrentPolygon(): PolygonData | null {
    return this.currentPolygon;
  }

  /**
   * Check if a polygon is currently plotted
   */
  hasPolygon(): boolean {
    return this.currentPolygon !== null;
  }

  /**
   * Get the polygon bounds
   */
  getBounds(): mapboxgl.LngLatBounds | null {
    return this.currentPolygon?.bounds || null;
  }
}

// Export singleton instance
export const polygonService = PolygonService.getInstance();
